import { SliderValue } from 'antd/lib/slider';
import { ChangeEvent, FC, useCallback, useState } from 'react';
import { Slider } from 'antd';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';

import { ContentLocalesI } from '~src/interfaces/session.types';
import { CardInfoContainer, CardSubject, ScaleContainer } from '~src/styles/shared.sc';
import CustomFormInput from '~src/shared/form/inputs/FormInput';
import CustomTextArea from '~src/shared/form/inputs/CustomTextArea';
import { validatorMaxLength1000 } from '~src/services/validators';
import { FieldType } from '~src/types';
import { CardContainer } from '~src/modules/questions/components/shared.sc';
import SecondaryButtonLoading from '~src/shared/buttons/loadingButtons/SecondaryButtonLoading';
import { ButtonColorTypes, ButtonShape } from '~src/shared/buttons/basicButtons/BaseButton.sc';
import { translateContent } from '~src/services/translationServices';
import { OnSubmitUpdateKeyResult } from '~src/store/okrs/keyResultStream/interfaces';
import FrozenInput from '~src/shared/form/inputs/FrozenInput';
import {
  formatKeyresultProgressable,
  keyresultDecimalToProgressable,
} from '~src/modules/okrs/manageObjectives/shared/utils';

import {
  KeyResultName,
  ProgressValueContainer,
  TargetValueContainer,
  ProgressTargetContainer,
  CommentContainer,
  SliderContainer,
} from './updateKeyResult.sc';

interface Props {
  id: string;
  inModal?: boolean;
  objectiveId: string;
  name: ContentLocalesI;
  progressValue?: number;
  targetValue: number;
  startValue: number;
  onSubmitUpdateKeyResult: OnSubmitUpdateKeyResult;
  submitLoading: boolean;
  comment?: string;
}

interface FormValues {
  comment?: string;
  newProgress?: number;
}

const UpdateKeyResult: FC<Props> = props => {
  const { name } = props;
  const [t] = useTranslation();

  const maxValue = Math.max(props.startValue, props.targetValue);
  const minValue = Math.min(props.startValue, props.targetValue);

  const [manualInputValue, setManualInputValue] = useState<string>(''); // TODO we should not need this
  const [inputError, setInputError] = useState<string | undefined>(undefined); // TODO this should be part of standard final-form validation (validate property of Form(

  const handleSubmit = (formValues: FormValues) => {
    if (formValues.newProgress || formValues.newProgress === 0) {
      props.onSubmitUpdateKeyResult({
        id: props.id,
        objectiveId: props.objectiveId,
        progressValue: formValues.newProgress,
        comment: formValues.comment,
      });
    }
  };

  const renderCommentField = useCallback(
    ({ input, placeholder, meta: { error, touched } }: FieldType) => (
      <CommentContainer>
        <CustomTextArea
          {...input}
          error={touched ? (error as string) : undefined}
          label={placeholder || 'UNKNOWN'}
          labelKey={'none'}
          minHeight={60}
          autoExpand
        />
      </CommentContainer>
    ),
    [],
  );

  const canBeInt = (value: number) => (value * 100) % 100 === 0;

  const isValidProgress = (value: any) =>
    typeof value === 'number' && !Number.isNaN(value) && value <= maxValue && value >= minValue;

  const toggleSliderAndRealValue = (value: number) =>
    props.startValue <= props.targetValue
      ? value
      : // Reducing to two decimal places because otherwise, floating errors can occur
        Math.round((props.startValue + props.targetValue - value) * 100) / 100;

  const formatter = (value: number) => <p>{formatKeyresultProgressable(toggleSliderAndRealValue(value))}</p>;

  return (
    <Form
      onSubmit={handleSubmit}
      enableReinitialize
      initialValues={{ newProgress: props.progressValue || 0 }}
    >
      {({ handleSubmit, form, values }) => {
        const newProgress: number | undefined = values.newProgress;

        const handleSliderValueChanged = (newValue: SliderValue) => {
          const value = Array.isArray(newValue) ? 0 : newValue;
          setInputError(undefined);
          form.change('newProgress', toggleSliderAndRealValue(canBeInt(value) ? Math.round(value) : value));
        };
        const changeProgressValueField = (event: ChangeEvent<HTMLInputElement>) => {
          setManualInputValue(event.target.value);
          setInputError(undefined);
          if (event.target.value === '') {
            setInputError('required');
            return;
          }
          const parsed = parseFloat(event.target.value);
          if (Number.isNaN(parsed)) {
            setInputError('invalid_progress_value');
            return;
          }
          const v = keyresultDecimalToProgressable(parsed);
          if (!isValidProgress(v)) {
            setInputError('invalid_progress_value');
            return;
          }
          handleSliderValueChanged(toggleSliderAndRealValue(v));
        };

        const currentProgress = toggleSliderAndRealValue(newProgress || props.startValue);

        return (
          <CardContainer $inModal={props.inModal} onSubmit={handleSubmit}>
            <CardInfoContainer>
              <div>
                <CardSubject>{t('x.okr.update_stream.update_key_result.key_result')}</CardSubject>
                <KeyResultName>{translateContent(name)}</KeyResultName>
              </div>
              <ProgressTargetContainer>
                <ProgressValueContainer>
                  <CustomFormInput
                    type="text"
                    error={inputError}
                    label={t('x.okr.update_stream.update_key_result.progress_value')}
                    labelKey={'none'}
                    onChange={changeProgressValueField}
                    errorParams={{
                      startValue: props.startValue,
                      targetValue: props.targetValue,
                    }}
                    value={manualInputValue}
                  />
                </ProgressValueContainer>
                <TargetValueContainer>
                  <FrozenInput
                    textAlign="right"
                    label={t('x.okr.update_stream.update_key_result.target_value')}
                    labelKey="x.okr.update_stream.update_key_result.target_value"
                    value={formatKeyresultProgressable(props.targetValue)}
                  />
                </TargetValueContainer>
              </ProgressTargetContainer>
              <SliderContainer>
                <Slider
                  step={props.startValue % 100 === 0 && props.targetValue % 100 === 0 ? 100 : 1}
                  min={minValue}
                  max={maxValue}
                  value={
                    inputError === undefined && isValidProgress(currentProgress) ? currentProgress : minValue
                  }
                  onChange={isValidProgress(currentProgress) ? handleSliderValueChanged : undefined}
                  tipFormatter={formatter}
                />
              </SliderContainer>
              <Field
                name="comment"
                label={t('x.okr.update_stream.update_key_result.comment')}
                placeholder={t('x.okr.update_stream.update_key_result.comment')}
                type="text"
                children={renderCommentField}
                validate={validatorMaxLength1000}
              />
            </CardInfoContainer>
            <ScaleContainer>
              <SecondaryButtonLoading
                isLoading={props.submitLoading}
                $buttonColor={ButtonColorTypes.White}
                $buttonShape={ButtonShape.Rectangle}
                disabled={inputError !== undefined}
              >
                <span data-translation="x.okr.update_stream.update_key_result.update">
                  {t('x.okr.update_stream.update_key_result.update')}
                </span>
              </SecondaryButtonLoading>
            </ScaleContainer>
          </CardContainer>
        );
      }}
    </Form>
  );
};
export default UpdateKeyResult;
